import React, { useState, useEffect } from 'react';

function HealthDot({ status }) {
  const colors = {
    healthy: 'bg-emerald-500 shadow-emerald-500/50',
    degraded: 'bg-yellow-500 shadow-yellow-500/50',
    down: 'bg-red-500 shadow-red-500/50',
    unknown: 'bg-slate-500',
  };
  return (
    <span className={`w-2.5 h-2.5 rounded-full shadow-sm ${colors[status] || colors.unknown} ${status === 'healthy' ? 'animate-pulse' : ''}`}></span>
  );
}

export default function ProviderHealth({ providers }) {
  const [health, setHealth] = useState({});

  // Simulate health checks (in production, this comes from the monitoring node)
  useEffect(() => {
    if (!providers || providers.length === 0) return;

    const checkHealth = async () => {
      const results = {};
      for (const p of providers) {
        const key = p.provider || p.wallet;
        try {
          const start = Date.now();
          const resp = await fetch(p.endpoint, { method: 'HEAD', mode: 'no-cors', signal: AbortSignal.timeout(3000) });
          const latency = Date.now() - start;
          results[key] = {
            status: latency < (p.promised_max_latency_ms || 5000) ? 'healthy' : 'degraded',
            latency,
            lastCheck: Date.now(),
          };
        } catch {
          results[key] = { status: 'unknown', latency: null, lastCheck: Date.now() };
        }
      }
      setHealth(results);
    };

    checkHealth();
    const t = setInterval(checkHealth, 60000); // Check every 60s
    return () => clearInterval(t);
  }, [providers]);

  if (!providers || providers.length === 0) return null;

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 sm:p-4 overflow-hidden">
      <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-3">Provider Health</h3>
      <div className="space-y-2">
        {providers.map((p, i) => {
          const key = p.provider || p.wallet;
          const h = health[key] || { status: 'unknown', latency: null };
          const slaNames = { 0: 'Basic', 1: 'Pro', 2: 'Enterprise' };

          return (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-800/50">
              <HealthDot status={h.status} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white truncate">{p.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{p.endpoint}</p>
              </div>
              <div className="text-right flex-shrink-0">
                {h.latency != null ? (
                  <p className={`text-xs font-mono ${h.latency < 500 ? 'text-emerald-400' : h.latency < 2000 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {h.latency}ms
                  </p>
                ) : (
                  <p className="text-xs text-slate-600">--</p>
                )}
                <p className="text-[9px] text-slate-600">{slaNames[p.sla_tier] || 'Basic'}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
